import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface User {
  id: number;
  name: string;
}

interface MultiUserSelectProps {
  users: User[];
  selectedIds: number[];
  excludedIds?: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const MultiUserSelect: React.FC<MultiUserSelectProps> = ({
  users,
  selectedIds,
  excludedIds = [],
  onChange,
  placeholder = 'Adicionar pessoa',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const userById = new Map(users.map(u => [u.id, u]));
  const visibleUsers = users.filter(u => !excludedIds.includes(u.id));

  function toggle(id: number) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(x => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  function remove(id: number) {
    onChange(selectedIds.filter(x => x !== id));
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {selectedIds.map(id => {
        const u = userById.get(id);
        if (!u) return null;
        return (
          <Badge key={id} variant="secondary" className="flex items-center gap-1">
            <span>{u.name}</span>
            {!disabled && (
              <button
                type="button"
                className="ml-1 rounded-sm hover:bg-muted-foreground/20"
                onClick={() => remove(id)}
                aria-label={`Remover ${u.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        );
      })}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={disabled} className="h-8">
            <Plus className="h-3 w-3 mr-1" />
            {placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Buscar usuário..." />
            <CommandList>
              <CommandEmpty>Nenhum usuário encontrado.</CommandEmpty>
              <CommandGroup>
                {visibleUsers.map(u => {
                  const selected = selectedIds.includes(u.id);
                  return (
                    <CommandItem key={u.id} onSelect={() => toggle(u.id)}>
                      <Check className={cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                      {u.name}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MultiUserSelect;
